import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ThreeDModel from "@/components/ThreeDModel";
import { api, mediaUrl, getAnonId, formatApiErrorDetail } from "@/lib/api";
import { takePendingUpload } from "@/lib/pendingUpload";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  UploadCloud, Loader2, Box, Image as ImageIcon, Save, Sparkles, RotateCcw,
  Plus, Trash2, Pencil, Camera,
} from "lucide-react";
import { ensureNotifyPermission, notify } from "@/lib/pwa";
import { toast } from "sonner";

const inferBeds = (rooms) => rooms.filter((r) => /bed/i.test(r.name)).length;
const inferBaths = (rooms) => rooms.filter((r) => /bath/i.test(r.name)).length;

export default function Generate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshBilling } = useBilling();
  const fileRef = useRef();
  const camRef = useRef();
  const [meta, setMeta] = useState({ building_types: [], finish_levels: [], regions: [] });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [floorplan, setFloorplan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [spec, setSpec] = useState(null);

  useEffect(() => {
    api.get("/building-types").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  // Consume a napkin photo snapped on the landing page and auto-generate.
  useEffect(() => {
    const pending = takePendingUpload();
    if (pending instanceof File) {
      pick(pending);
      generate(pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pick = (f) => {
    if (!f) return;
    setFile(f);
    setResult(null);
    setFloorplan(null);
    if (f.type.startsWith("image/")) setPreview(URL.createObjectURL(f));
    else setPreview(null);
  };

  const generate = async (override) => {
    const f = override instanceof File ? override : file;
    if (!f) return toast.error("Choose a blueprint or sketch first");
    ensureNotifyPermission();
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/generate-3d", fd, { headers: { "X-Anon-Id": getAnonId() } });
      const fp = data.floorplan || { rooms: [] };
      setResult(data);
      setFloorplan(fp);
      setSpec({
        name: "My 3D Home",
        building_type: "single_family_home",
        square_footage: fp.total_sqft_est || 1500,
        floors: fp.floors || 1,
        bedrooms: inferBeds(fp.rooms || []),
        bathrooms: inferBaths(fp.rooms || []),
        finish_level: "standard",
        region: "National Average",
      });
      toast.success("3D model generated");
      notify("Your 3D model is ready", "Costobuild turned your blueprint into a 3D model + estimate.");
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(String(e.response.data.detail));
        navigate(user ? "/billing" : "/register");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const setRoomName = (i, name) =>
    setFloorplan((fp) => ({ ...fp, rooms: fp.rooms.map((r, idx) => (idx === i ? { ...r, name } : r)) }));
  const deleteRoom = (i) => setFloorplan((fp) => ({ ...fp, rooms: fp.rooms.filter((_, idx) => idx !== i) }));
  const addRoom = () =>
    setFloorplan((fp) => ({ ...fp, rooms: [...(fp.rooms || []), { name: "New Room", x: 35, y: 35, w: 24, h: 20 }] }));

  const setSpecField = (k, v) => setSpec((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/generations/${result.generation_id}/save`, {
        name: spec.name || "My 3D Home",
        building_type: spec.building_type,
        square_footage: Number(spec.square_footage) || 1500,
        floors: Number(spec.floors) || 1,
        bedrooms: Number(spec.bedrooms) || 0,
        bathrooms: Number(spec.bathrooms) || 0,
        finish_level: spec.finish_level,
        region: spec.region,
        floorplan: { ...floorplan, total_sqft_est: Number(spec.square_footage) || floorplan.total_sqft_est },
      });
      await refreshBilling();
      toast.success("Saved to your projects");
      navigate(`/projects/${data.project_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save — try again");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setFloorplan(null);
    setSpec(null);
  };

  const field = "mt-1.5 rounded-sm border-border bg-transparent focus:border-primary focus:ring-primary";
  const lbl = "font-mono2 text-xs uppercase tracking-widest text-muted-foreground";

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
        <div className="max-w-3xl">
          <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary">AI Blueprint → 3D</p>
          <h1 className="mt-2 font-heading text-4xl font-extrabold tracking-tighter sm:text-5xl">
            Turn any blueprint into a 3D model.
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Upload a floor plan, blueprint or even a rough sketch. Our AI reads the layout, builds an interactive
            3D model, and renders a photorealistic vision of the finished building.
          </p>
          <p className="mt-2 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">First 2 generations are free</p>
          {!user && (
            <p className="mt-2 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">
              Free preview · <Link to="/register" className="text-primary hover:underline">create an account</Link> to save your models
            </p>
          )}
        </div>

        {!result && (
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <div className="space-y-3">
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
                data-testid="upload-dropzone"
                className="flex min-h-[260px] cursor-pointer flex-col items-center justify-center border-2 border-dashed border-border p-8 text-center transition-colors hover:border-primary/60"
              >
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf" hidden
                  onChange={(e) => pick(e.target.files?.[0])} data-testid="file-input" />
                <UploadCloud className="h-12 w-12 text-primary" />
                <p className="mt-4 font-heading text-lg font-semibold">{file ? file.name : "Drop your blueprint here"}</p>
                <p className="mt-1 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">PNG · JPG · WEBP · PDF · up to 12MB</p>
              </div>
              <input ref={camRef} type="file" accept="image/*" capture="environment" hidden
                onChange={(e) => pick(e.target.files?.[0])} data-testid="camera-input" />
              <Button type="button" variant="outline" onClick={() => camRef.current?.click()} data-testid="camera-btn"
                className="w-full rounded-sm border-border font-mono2 uppercase tracking-widest">
                <Camera className="mr-2 h-4 w-4" /> Snap a Photo
              </Button>
            </div>

            <div className="flex flex-col justify-between border border-border p-6">
              <div>
                <h3 className={lbl}>Preview</h3>
                <div className="mt-3 flex min-h-[200px] items-center justify-center border border-border bg-[#050a16]">
                  {preview ? (
                    <img src={preview} alt="preview" className="max-h-[240px] w-full object-contain" />
                  ) : (
                    <span className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">
                      {file ? "PDF selected" : "No file selected"}
                    </span>
                  )}
                </div>
              </div>
              <Button onClick={generate} disabled={!file || loading} data-testid="generate-3d-btn"
                className="mt-5 w-full rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating (20-40s)…</> : <><Sparkles className="mr-2 h-4 w-4" /> Generate 3D</>}
              </Button>
            </div>
          </div>
        )}

        {result && floorplan && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-10">
            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <Box className="h-4 w-4 text-primary" />
                  <h3 className={lbl}>Interactive 3D Model — drag to orbit</h3>
                </div>
                <ThreeDModel floorplan={floorplan} />
                {floorplan.generated_with === "fallback" && (
                  <p className="mt-2 font-mono2 text-xs text-accent" data-testid="fallback-warning">
                    Heads up — the AI couldn't fully read this drawing, so this is a best-guess layout. Try a clearer blueprint.
                  </p>
                )}
              </div>
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-primary" />
                  <h3 className={lbl}>Photorealistic Render</h3>
                </div>
                <div className="flex min-h-[300px] items-center justify-center overflow-hidden border border-border bg-[#050a16]">
                  {result.render_url ? (
                    <img src={mediaUrl(result.render_url)} alt="render" className="h-full w-full object-cover" data-testid="render-image" />
                  ) : (
                    <span className="p-8 text-center font-mono2 text-xs uppercase tracking-widest text-muted-foreground">
                      Render unavailable — the 3D model is ready.
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Room editor + spec */}
            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              <div className="border border-border p-6" data-testid="room-editor">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Pencil className="h-4 w-4 text-primary" />
                    <h3 className={lbl}>Edit rooms — model updates live</h3>
                  </div>
                  <Button type="button" size="sm" onClick={addRoom} data-testid="add-room-editor-btn"
                    className="rounded-sm bg-primary text-black hover:bg-primary/90">
                    <Plus className="mr-1 h-4 w-4" /> Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {floorplan.rooms.map((r, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`room-edit-row-${i}`}>
                      <Input value={r.name} onChange={(e) => setRoomName(i, e.target.value)}
                        data-testid={`room-name-${i}`} className="rounded-sm border-border bg-transparent focus:border-primary focus:ring-primary" />
                      <Button type="button" variant="outline" size="icon" onClick={() => deleteRoom(i)}
                        data-testid={`room-delete-${i}`} className="shrink-0 rounded-sm border-border text-muted-foreground hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {floorplan.rooms.length === 0 && (
                    <p className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">No rooms — add one.</p>
                  )}
                </div>
              </div>

              <div className="border border-border p-6">
                <h3 className={`${lbl} mb-4`}>Project details</h3>
                <div className="space-y-4">
                  <div>
                    <Label className={lbl}>Project name</Label>
                    <Input value={spec.name} onChange={(e) => setSpecField("name", e.target.value)} data-testid="save-project-name" className={field} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className={lbl}>Building type</Label>
                      <Select value={spec.building_type} onValueChange={(v) => setSpecField("building_type", v)}>
                        <SelectTrigger data-testid="save-building-type" className={field}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {meta.building_types.map((b) => <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={lbl}>Square footage</Label>
                      <Input type="number" value={spec.square_footage} onChange={(e) => setSpecField("square_footage", e.target.value)} data-testid="save-sqft" className={field} />
                    </div>
                    <div>
                      <Label className={lbl}>Bedrooms</Label>
                      <Input type="number" value={spec.bedrooms} onChange={(e) => setSpecField("bedrooms", e.target.value)} data-testid="save-bedrooms" className={field} />
                    </div>
                    <div>
                      <Label className={lbl}>Bathrooms</Label>
                      <Input type="number" value={spec.bathrooms} onChange={(e) => setSpecField("bathrooms", e.target.value)} data-testid="save-bathrooms" className={field} />
                    </div>
                    <div>
                      <Label className={lbl}>Finish level</Label>
                      <Select value={spec.finish_level} onValueChange={(v) => setSpecField("finish_level", v)}>
                        <SelectTrigger data-testid="save-finish" className={field}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {meta.finish_levels.map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={lbl}>Region</Label>
                      <Select value={spec.region} onValueChange={(v) => setSpecField("region", v)}>
                        <SelectTrigger data-testid="save-region" className={field}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {meta.regions.map((rg) => <SelectItem key={rg} value={rg}>{rg}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3 border border-border p-5">
              {user ? (
                <Button onClick={save} disabled={saving} data-testid="save-generation-btn"
                  className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save to Projects & Estimate
                </Button>
              ) : (
                <Link to="/register" data-testid="signup-to-save">
                  <Button className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                    Sign up to save this model
                  </Button>
                </Link>
              )}
              <Button variant="outline" onClick={reset} data-testid="generate-another-btn"
                className="rounded-sm border-border font-mono2 uppercase tracking-widest">
                <RotateCcw className="mr-2 h-4 w-4" /> Try Another
              </Button>
              {result.remaining_free != null && (
                <span className="ml-auto font-mono2 text-xs uppercase tracking-widest text-muted-foreground">
                  {result.remaining_free} free {result.remaining_free === 1 ? "try" : "tries"} left
                </span>
              )}
            </div>
          </motion.div>
        )}
      </div>
      <Footer />
    </div>
  );
}
